import type { DeepDiveDetail } from "@/types"

/*
 * The four boxes the Deep dive fills in per card, from the prototype's FIELDS.
 *
 * Ordered, because they are asked in this order and the leave-behind prints them
 * in it. Keyed to `DeepDiveDetail`, so a field that is renamed on the document
 * cannot quietly keep rendering under its old label.
 */

export interface DeepDiveField {
  key: keyof DeepDiveDetail
  label: string
  /** The placeholder — a standard for the answer, not a description of it. */
  hint: string
}

export const DEEP_DIVE_FIELDS: readonly DeepDiveField[] = [
  {
    key: "whoseJob",
    label: "Whose job gets easier",
    hint: "Name a role. If you can't, it isn't real yet.",
  },
  {
    key: "painToday",
    label: "The pain today, their words",
    hint: "Quote them. Verbatim beats paraphrase.",
  },
  {
    key: "metric",
    label: "What number moves",
    hint: "Hours, euros, minutes-to-detect, churn.",
  },
  {
    key: "whereItLives",
    label: "Where it lives in the product",
    hint: "A screen they already have, or a new one?",
  },
] as const

export const DEEP_COPY = {
  heading: "Deep dive",
  objective:
    "Go deep on the keepers. One card at a time: what it solves, what it costs, where it lives.",
  /*
   * Said out loud on the screen because it changes what an empty box means. A
   * blank is not an unfinished form, it is the room discovering it cannot answer
   * the question yet — which is the most useful thing the round produces.
   */
  emptyIsAFinding:
    "An empty box means there's still work to do about this use case. That's a finding too.",
  stripHintFacilitator: "Tap a card to bring it up for the room.",
  stripHintRoom: "The facilitator picks which card the room is on.",
  position: (index: number, total: number) => `${index + 1} of ${total} use cases`,
  /** No Must have was filed, so the dive falls back to the top of the shortlist. */
  noMustHaves: "Nothing was filed as a Must have, so the dive starts at the top of the shortlist.",
  notTakenFurther: "Not taken further in the session.",
} as const
